import React, { useEffect } from 'react';
import styles from './styles.module.scss';

const TermsOfUse = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.container}>
      <div>
        <p className={styles.footnote}>Effective date: March 9, 2026</p>
        <h1>Terms of Use</h1>
      </div>

      <div>
        These Terms of Use (the "<b>Terms</b>") apply to your access and use of
        the mobile application "<b>Voery</b>" (the "<b>App</b>"), the website{' '}
        <a href='https://voery.app/'>voery.app</a>, and all related services,
        features, and content (collectively the "<b>Services</b>") provided by{' '}
        <b>POFKARIO HOLDINGS LIMITED</b>, a company registered in Cyprus with
        registration no. ΗΕ 467843 and registered address Thessalonikis 43,
        office/flat 301 Thessaloniki Court, 3025 Limassol, Cyprus (collectively
        referred to as "<b>Company</b>", "<b>we</b>", or "<b>us</b>").
      </div>

      <div>
        <p>
          Please note that these Terms are an agreement between you and the
          Company only. The Terms are not entered into with Apple, Inc., Google,
          Inc., or any of their subsidiaries.
        </p>
        <p>
          If you do not agree to these Terms, including the mandatory arbitration
          provision in Section 11, do not use Voery. Your continued use of the
          Services confirms your acceptance of these Terms.
        </p>
      </div>

      <div>
        <p>
          <b>1. Changes to the Terms</b>
        </p>
        <p>
          We may modify these Terms at any time. If changes materially affect
          your rights, we will notify you via email or through the Services. Your
          continued use of Voery after an update constitutes acceptance of the
          new Terms.
        </p>
      </div>

      <div>
        <p>
          <b>2. Service Description &amp; Disclaimers</b>
        </p>
        <p>Voery provides AI-powered communication tools, including:</p>
        <ul>
          <li>
            <b>Live Call Translation:</b> Real-time text translation of calls
            via shared links.
          </li>
          <li>
            <b>Face-to-Face Mode:</b> Instant on-screen subtitles for in-person
            dialogue.
          </li>
          <li>
            <b>AI Language Detection:</b> Automatic identification of 150+
            languages.
          </li>
        </ul>
        <p>
          <b>Important Disclaimers:</b>
        </p>
        <ul>
          <li>
            <b>AI Accuracy:</b> Translation is generated by AI. We do not
            guarantee 100% accuracy. Quality may vary based on accents,
            background noise, or technical limitations.
          </li>
          <li>
            <b>No Emergency Use:</b> <b>DO NOT</b> use Voery for emergency
            services (e.g., calling 911), medical consultations, or legal
            proceedings where absolute precision is required.
          </li>
          <li>
            <b>Third-Party Consent:</b> When using Live Call Translation, you
            are solely responsible for ensuring that the other party consents to
            having the conversation processed by AI.
          </li>
          <li>
            <b>Assumption of Risk:</b> You use the translation outputs at your
            own risk. The Company is not liable for any misunderstandings or
            damages resulting from translation errors.
          </li>
        </ul>
      </div>

      <div>
        <p>
          <b>3. Subscriptions, Credits &amp; Billing</b>
        </p>
        <p>
          All regular subscriptions automatically renew at the end of your
          then-current subscription period unless you turn off auto-renewal or
          cancel your subscription at least 24 hours before the subscription
          period ends. You can cancel your regular subscription at any time, and
          you will still be able to access the premium features until the end of
          the subscription period. If you cancel your subscription during your
          subscription period, you will not be refunded for the unused period.
        </p>
        <p>
          We may offer <b>commitment subscriptions</b> providing you with the
          option to subscribe to Voery for a predetermined duration at a reduced
          price per month. After the commitment period expires, the commitment
          subscription converts to a regular subscription which auto-renews
          monthly at the monthly subscription price applicable at the time of
          renewal. Generally, commitment subscriptions cannot be canceled until
          the commitment period expires, unless one of the exceptions mentioned
          in our Refund Policy applies. If none of the exceptions apply, a
          non-refundable cancellation fee may apply to cancel before expiration
          of the commitment period.
        </p>
        <p>
          For the purposes of this Section, the term "<b>subscription(s)</b>"
          covers both regular subscriptions and commitment subscriptions, unless
          the contrary is specified. Subscriptions auto-renew even if you are
          not actively using the App. If you have any difficulties, concerns, or
          questions about your subscription, please contact us at{' '}
          <a href='mailto:support@voery.app'>support@voery.app.</a>
        </p>
      </div>

      <div>
        <div>
          <b>3.1. Voery Subscription. </b>We offer subscriptions that grant you
          access to special features and content, including, but not limited to,
          real-time AI call translation, face-to-face translation mode, and a
          recurring balance of credits. You agree that your purchases are not
          contingent on the delivery of any future functionality or features.
        </div>
      </div>

      <div>
        <div>
          <b>3.2. Billing. </b>Payments are processed via third-party providers.
          When the subscription auto-renews, the payment is charged at the
          beginning of each billing period to the payment method used during the
          initial purchase. By completing a transaction, you authorize the secure
          tokenization of your payment method for applicable renewal fees and
          subsequent one-time credit top-ups initiated within the Services.
        </div>
      </div>

      <div>
        <div>
          <b>3.3. Credit System &amp; Consumption. </b>Voery operates on a
          credit-based model.
        </div>
        <ul>
          <li>
            <b>Subscription Credits:</b> Granted as part of your recurring plan.
            These credits <b>expire and reset to zero</b> at the end of each
            billing cycle if not used.
          </li>
          <li>
            <b>One-time Purchases:</b> Credits purchased as individual packages
            (top-ups) are permanent and do not expire.
          </li>
          <li>
            <b>Deduction Priority:</b> The system always deducts Subscription
            Credits first. One-time Credits are consumed only after the
            subscription credit balance is fully exhausted.
          </li>
          <li>
            <b>Service Continuity:</b> If your total credit balance reaches zero
            during an active session, the service will be disconnected
            immediately to prevent unauthorized overages.
          </li>
        </ul>
      </div>

      <div>
        <div>
          <b>3.4. Intro offers. </b>We may offer users an intro offer period to
          experience premium features at a reduced price. During the intro offer,
          you may either cancel your subscription no later than 24 hours before
          the period ends, or continue using the premium subscription at its
          normal price.
        </div>
      </div>

      <div>
        <div>
          <b>3.5. Renewal and Cancellation. </b>Your paid subscription will
          automatically renew unless canceled. You may cancel automatic renewals
          at any time prior to the renewal date via your account settings or by
          emailing us at{' '}
          <a href='mailto:support@voery.app'>support@voery.app.</a> Please note
          that your subscription <b>does not</b> cancel automatically if you
          delete the mobile application.
        </div>
      </div>

      <div>
        <div>
          <b>3.6. Refunds. </b>Generally, your purchases are non-refundable.
          Refund requests are evaluated on a case-by-case basis (e.g., technical
          failure where service was not provided) and should be directed to{' '}
          <a href='mailto:support@voery.app'>support@voery.app.</a>
        </div>
      </div>

      <div>
        <div>
          <b>3.7. Changes. </b>We may change our subscription plans or recurring
          fees and will communicate any price changes to you in advance. Price
          changes will take effect at the start of the next subscription period.
        </div>
      </div>

      <div>
        <p>
          <b>4. Registration and Eligibility</b>
        </p>
        <p>
          You must be at least 18 years old to create an account. You are
          responsible for all activity on your account and for maintaining the
          confidentiality of your password.
        </p>
      </div>

      <div>
        <p>
          <b>5. Your Use of the Services</b>
        </p>
        <p>
          You agree <b>NOT</b> to:
        </p>
        <ul>
          {[
            'Reverse engineer, decompile, or attempt to extract the source code of the App.',
            'Use the App for illegal surveillance or unauthorized recording of conversations.',
            'Upload or transmit content that is offensive, defamatory, or infringes on intellectual property.',
            'Bypass any limits or technical protections of the Services.',
          ].map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </div>

      <div>
        <p>
          <b>6. Limited License</b>
        </p>
        <p>
          We grant you a personal, worldwide, revocable, and non-exclusive
          license to use Voery for personal, non-commercial purposes. All rights,
          title, and interest in the App, "Voery" branding, and proprietary AI
          implementations remain with the Company.
        </p>
      </div>

      <div>
        <p>
          <b>7. Privacy</b>
        </p>
        <p>
          Your use of the Services is also governed by our Privacy Policy,
          available at{' '}
          <a href='https://voery.app/privacy'>voery.app/privacy.</a>
        </p>
      </div>

      <div>
        <p>
          <b>8. Warranty Disclaimer</b>
        </p>
        <p>
          THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. THE
          COMPANY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING THE
          ACCURACY OF TRANSLATIONS AND UNINTERRUPTED AVAILABILITY.
        </p>
      </div>

      <div>
        <p>
          <b>9. Limitation of Liability</b>
        </p>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY SHALL NOT BE
          LIABLE FOR ANY INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES. OUR
          TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU TO THE COMPANY
          IN THE LAST 12 MONTHS OR $100, WHICHEVER IS GREATER.
        </p>
      </div>

      <div>
        <p>
          <b>10. Indemnity</b>
        </p>
        <p>
          You agree to indemnify and hold the Company harmless from any claims,
          losses, or expenses (including legal fees) arising from your use of the
          Services or violation of these Terms.
        </p>
      </div>

      <div>
        <p>
          <b>11. Dispute Resolution &amp; Arbitration</b>
        </p>
        <p>
          <b>11.1. Negotiation:</b> Before filing a formal claim, you agree to
          try to resolve the dispute informally by contacting{' '}
          <a href='mailto:support@voery.app'>support@voery.app.</a>
        </p>
        <p>
          <b>11.2. Binding Arbitration:</b> If not resolved within 45 days, the
          dispute will be settled through binding arbitration (AAA for US
          residents, ICDR for non-US residents).
        </p>
        <p>
          <b>11.3. Class Action Waiver:</b> You agree to resolve disputes on an
          individual basis and waive any right to participate in a class-action
          lawsuit or class-wide arbitration.
        </p>
      </div>

      <div>
        <p>
          <b>12. Governing Law</b>
        </p>
        <p>
          These Terms are governed by the laws of the Republic of Cyprus. Any
          legal actions not subject to arbitration shall be brought exclusively
          in the courts of Cyprus.
        </p>
      </div>

      <div>
        <p>
          <b>13. Contact Us</b>
        </p>
        <p>
          For any questions regarding these Terms, please contact:
        </p>
        <ul>
          <li>
            Email:{' '}
            <a href='mailto:support@voery.app'>support@voery.app</a>
          </li>
          <li>
            Address: Thessalonikis 43, office/flat 301 Thessaloniki Court, 3025
            Limassol, Cyprus
          </li>
        </ul>
      </div>
    </div>
  );
};

export default TermsOfUse;